import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Info, Check, X } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { ImageUploader } from '../../components/admin';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// COMPONENTES DE SELECTOR REUTILIZABLES
// ============================================

// Selector simple (dropdown)
const SelectField = ({ label, name, value, onChange, options, required, helpText }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label} {required && <span className="text-amber-500">*</span>}
    </label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500 transition-colors"
      required={required}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
  </div>
);

// Temporadas en el orden del año. Se guardan en `tags` del producto, que el
// backend ya acepta y filtra (/api/products?tags=Otoño), sin campo nuevo.
const SEASON_TAGS = ['Primavera', 'Verano', 'Otoño', 'Invierno'];

// Multi-select con chips/badges
const MultiSelectField = ({ label, name, values, onChange, options, helpText }) => {
  const toggleOption = (optionValue) => {
    const currentValues = values || [];
    if (currentValues.includes(optionValue)) {
      onChange(name, currentValues.filter(v => v !== optionValue));
    } else {
      onChange(name, [...currentValues, optionValue]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const isSelected = (values || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggleOption(opt)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                isSelected
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {isSelected && <Check size={14} className="inline mr-1" />}
              {opt}
            </button>
          );
        })}
      </div>
      {helpText && <p className="text-xs text-gray-500 mt-2">{helpText}</p>}
    </div>
  );
};

// Input de texto
const TextField = ({ label, name, value, onChange, placeholder, required, helpText, readOnly }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label} {required && <span className="text-amber-500">*</span>}
    </label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
      required={required}
      readOnly={readOnly}
    />
    {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
  </div>
);

// Input numérico
const NumberField = ({ label, name, value, onChange, min, max, required, helpText, readOnly }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">
      {label} {required && <span className="text-amber-500">*</span>}
    </label>
    <input
      type="number"
      name={name}
      value={value}
      onChange={onChange}
      min={min}
      max={max}
      className={`w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500 transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
      required={required}
      readOnly={readOnly}
    />
    {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
  </div>
);

// Textarea
const TextareaField = ({ label, name, value, onChange, placeholder, rows = 3, helpText }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 resize-none transition-colors"
    />
    {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
  </div>
);

// Section Card
const SectionCard = ({ title, description, icon, children }) => (
  <div className="bg-gray-800 rounded-xl p-6" data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start gap-3 mb-5">
      {icon && <div className="p-2 bg-amber-500/10 rounded-lg text-amber-500">{icon}</div>}
      <div>
        <h3 className="text-lg font-medium text-white">{title}</h3>
        {description && <p className="text-sm text-gray-400 mt-0.5">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchWithAuth } = useAdminAuth();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);
  const [brands, setBrands] = useState([]);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    // Identificación
    product_code: '',
    // Inspiración
    inspired_by_name: '',
    inspired_by_brand: '',
    // Clasificación básica
    perfume_type: 'Diseñador',
    gender: 'Hombre',
    intensity: 'Moderada',
    concentration: 'Eau de Parfum',
    // Nuevos campos multi-select
    olfactory_family: [],
    mood: [],
    popularity: [],
    // Temporada: va en tags (Primavera, Verano, Otoño, Invierno)
    tags: [],
    // Auto-generados
    name: '',
    slug: '',
    // Descripción
    short_description: '',
    long_description: '',
    // Notas olfativas (pirámide olfativa)
    notes_top: '',      // Notas de entrada/salida
    notes_heart: '',    // Notas de corazón
    notes_base: '',     // Notas de fondo
    // Precio e inventario
    size_ml: 100,
    price: 399,
    price_40ml: 250,
    stock: 0,
    stock_40ml: 0,
    stock_10ml: 0,
    // Imágenes
    main_image: '',
    gallery_images: [],
    // Visibilidad
    is_active: true
  });

  const loadBrands = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/brands/all');
      if (res.ok) {
        const data = await res.json();
        setBrands(data.brands || []);
      }
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  }, [fetchWithAuth]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/products/filter-options`);
      if (res.ok) {
        const data = await res.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, []);

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`/api/admin/products/${id}`);
      if (res.ok) {
        const data = await res.json();
        // Asegurar que los campos de array existan
        setFormData({
          ...data,
          olfactory_family: data.olfactory_family || [],
          mood: data.mood || [],
          popularity: data.popularity || [],
          tags: data.tags || [],
          notes_top: data.notes_top || '',
          notes_heart: data.notes_heart || '',
          notes_base: data.notes_base || '',
          price_40ml: data.price_40ml || 250,
          stock_40ml: data.stock_40ml || 0,
          stock_10ml: data.stock_10ml || 0
        });
      } else {
        toast.error('Producto no encontrado');
        navigate('/admin/products');
      }
    } catch (error) {
      toast.error('Error al cargar producto');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, id, navigate]);

  // Cargar opciones de filtros al montar
  useEffect(() => {
    loadFilterOptions();
    loadBrands();
    if (isEditing) {
      loadProduct();
    }
  }, [loadFilterOptions, loadBrands, loadProduct, isEditing]);

  // Auto-generar nombre y slug
  useEffect(() => {
    if (formData.product_code && formData.inspired_by_name) {
      const name = `${formData.product_code} - Inspirado en ${formData.inspired_by_name}`;
      const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, name, slug }));
    }
  }, [formData.product_code, formData.inspired_by_name]);

  // Auto-establecer precio según tipo
  useEffect(() => {
    const price = formData.perfume_type === 'Nicho' ? 499 : 399;
    setFormData(prev => ({ ...prev, price }));
  }, [formData.perfume_type]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelectChange = (name, values) => {
    setFormData(prev => ({
      ...prev,
      [name]: values
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!formData.product_code || !formData.inspired_by_name || !formData.inspired_by_brand) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      setSaving(true);
      
      const url = isEditing 
        ? `/api/admin/products/${id}` 
        : '/api/admin/products';
      
      const res = await fetchWithAuth(url, {
        method: isEditing ? 'PUT' : 'POST',
        body: formData
      });

      if (res.ok) {
        toast.success(isEditing ? 'Producto actualizado' : 'Producto creado');
        navigate('/admin/products');
      } else {
        const error = await res.json();
        // Handle Pydantic validation errors
        if (error.detail && Array.isArray(error.detail)) {
          const messages = error.detail.map(err => {
            const field = err.loc?.join('.') || 'campo';
            return `${field}: ${err.msg}`;
          }).join(', ');
          toast.error(messages || 'Error de validación');
        } else if (typeof error.detail === 'string') {
          toast.error(error.detail);
        } else {
          toast.error('Error al guardar el producto');
        }
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  // Opciones para selectores
  const perfumeTypeOptions = [
    { value: 'Diseñador', label: 'Diseñador ($399 MXN)' },
    { value: 'Nicho', label: 'Nicho ($499 MXN)' }
  ];

  const genderOptions = [
    { value: 'Hombre', label: 'Hombre' },
    { value: 'Mujer', label: 'Mujer' },
    { value: 'Unisex', label: 'Unisex' }
  ];

  const intensityOptions = [
    { value: 'Sutil', label: 'Sutil' },
    { value: 'Intenso', label: 'Intenso' },
    { value: 'Monstruosa', label: 'Monstruosa' }
  ];

  const concentrationOptions = [
    { value: 'Eau de Cologne', label: 'Eau de Cologne' },
    { value: 'Eau de Toilette', label: 'Eau de Toilette' },
    { value: 'Eau de Parfum', label: 'Eau de Parfum' },
    { value: 'Parfum', label: 'Parfum' }
  ];

  return (
    <div className="max-w-4xl" data-testid="admin-product-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/products')}
          className="p-2 text-gray-400 hover:text-white transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h2 className="text-2xl font-semibold text-white">
            {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
          </h2>
          <p className="text-gray-400 text-sm">
            {isEditing ? 'Modifica los detalles del producto' : 'Agrega un nuevo perfume al catálogo'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* ============================================ */}
        {/* SECCIÓN 1: IDENTIFICACIÓN */}
        {/* ============================================ */}
        <SectionCard 
          title="Identificación" 
          description="Código único y datos de inspiración del perfume"
          icon={<Info size={20} />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Código del Producto"
              name="product_code"
              value={formData.product_code}
              onChange={handleChange}
              placeholder="Ej: X001-M, A015-D"
              required
              helpText="Identificador único de tu catálogo"
            />

            <SelectField
              label="Tipo de Perfume"
              name="perfume_type"
              value={formData.perfume_type}
              onChange={handleChange}
              options={perfumeTypeOptions}
              required
              helpText="Define el precio automáticamente"
            />

            <TextField
              label="Inspirado en (Nombre)"
              name="inspired_by_name"
              value={formData.inspired_by_name}
              onChange={handleChange}
              placeholder="Ej: Sauvage, Good Girl, EROS"
              required
            />

            <SelectField
              label="Inspirado en (Marca)"
              name="inspired_by_brand"
              value={formData.inspired_by_brand}
              onChange={handleChange}
              options={[
                { value: '', label: 'Selecciona una marca...' },
                ...brands.map(b => ({ value: b.name, label: b.name }))
              ]}
              required
              helpText="Marcas del catálogo"
            />
          </div>

          {/* Nombre generado automáticamente */}
          <div className="mt-4 p-3 bg-gray-700/50 rounded-lg border border-gray-600">
            <p className="text-xs text-gray-400 mb-1">Nombre generado automáticamente:</p>
            <p className="text-white font-medium">{formData.name || 'Se generará al completar código y nombre'}</p>
          </div>
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 2: CLASIFICACIÓN BÁSICA */}
        {/* ============================================ */}
        <SectionCard 
          title="Clasificación Básica" 
          description="Género, intensidad y concentración del perfume"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SelectField
              label="Género"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              options={genderOptions}
              helpText="¿Para quién es este perfume?"
            />

            <SelectField
              label="Intensidad"
              name="intensity"
              value={formData.intensity}
              onChange={handleChange}
              options={intensityOptions}
              helpText="Potencia de la fragancia"
            />
          </div>
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 3: FAMILIA OLFATIVA (Multi-select) */}
        {/* ============================================ */}
        <SectionCard 
          title="Familia Olfativa" 
          description="Selecciona todas las familias que apliquen a este perfume"
        >
          <MultiSelectField
            label=""
            name="olfactory_family"
            values={formData.olfactory_family}
            onChange={handleMultiSelectChange}
            options={filterOptions?.olfactory_families || [
              'Cítrico', 'Fresco', 'Dulce', 'Madera', 'Floral'
            ]}
            helpText="Puedes seleccionar múltiples opciones"
          />
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 4: MOOD / OCASIÓN (Multi-select) */}
        {/* ============================================ */}
        <SectionCard 
          title="Mood / Ocasión" 
          description="¿Para qué momentos es ideal este perfume?"
        >
          <MultiSelectField
            label=""
            name="mood"
            values={formData.mood}
            onChange={handleMultiSelectChange}
            options={(filterOptions?.moods || [
              'Perfecto para la noche',
              'Para el día a día',
              'Elegante',
              'Sexy'
            ]).filter(m => m !== 'Elegante')}
            helpText="Ayuda a los clientes a encontrar el perfume perfecto para cada ocasión"
          />
        </SectionCard>

        {/* ============================================ */}
        {/* TEMPORADA (Multi-select, se guarda en tags) */}
        {/* ============================================ */}
        <SectionCard
          title="Temporada"
          description="Edición de temporada en la que aparece este perfume"
        >
          <MultiSelectField
            label=""
            name="tags"
            values={(formData.tags || []).filter((t) => SEASON_TAGS.includes(t))}
            onChange={(name, seasons) =>
              handleMultiSelectChange(name, [
                ...(formData.tags || []).filter((t) => !SEASON_TAGS.includes(t)),
                ...seasons,
              ])
            }
            options={SEASON_TAGS}
            helpText="Puedes elegir más de una. Se guarda junto con las demás etiquetas del producto."
          />
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 5: POPULARIDAD (Multi-select) */}
        {/* ============================================ */}
        <SectionCard 
          title="Etiquetas de Popularidad" 
          description="Destaca este producto en secciones especiales"
        >
          <MultiSelectField
            label=""
            name="popularity"
            values={formData.popularity}
            onChange={handleMultiSelectChange}
            options={filterOptions?.popularities || [
              'Más vendidos',
              'Nuevo Lanzamiento'
            ]}
            helpText="Estos tags controlan las insignias del producto y en qué secciones aparece"
          />
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 6: DESCRIPCIÓN */}
        {/* ============================================ */}
        <SectionCard 
          title="Descripción" 
          description="Textos que verán los clientes"
        >
          <div className="space-y-4">
            <TextField
              label="Descripción Corta"
              name="short_description"
              value={formData.short_description}
              onChange={handleChange}
              placeholder="Fragancia fresca y especiada perfecta para el día..."
              helpText="Máximo 150 caracteres, aparece en las tarjetas"
            />

            <TextareaField
              label="Descripción Larga"
              name="long_description"
              value={formData.long_description}
              onChange={handleChange}
              rows={4}
              placeholder="Descripción detallada del perfume, notas olfativas, ocasiones de uso..."
              helpText="Descripción completa para la página del producto"
            />

            {/* Pirámide Olfativa */}
            <div className="pt-4 border-t border-gray-700">
              <p className="text-sm font-medium text-amber-500 mb-3">Pirámide Olfativa</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextField
                  label="Notas de Entrada"
                  name="notes_top"
                  value={formData.notes_top}
                  onChange={handleChange}
                  placeholder="Ej: Bergamota, Limón, Menta"
                  helpText="Primeras notas que se perciben"
                />
                <TextField
                  label="Notas de Corazón"
                  name="notes_heart"
                  value={formData.notes_heart}
                  onChange={handleChange}
                  placeholder="Ej: Rosa, Jazmín, Cedro"
                  helpText="Notas principales de la fragancia"
                />
                <TextField
                  label="Notas de Base"
                  name="notes_base"
                  value={formData.notes_base}
                  onChange={handleChange}
                  placeholder="Ej: Almizcle, Vainilla, Ámbar"
                  helpText="Notas de fondo, las más duraderas"
                />
              </div>
            </div>
          </div>
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 7: PRECIO E INVENTARIO */}
        {/* ============================================ */}
        <SectionCard 
          title="Precio e Inventario" 
          description="Configuración de precio y stock por presentación"
        >
          {/* Presentación 100ml */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-amber-500 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Presentación 100ml
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label="Precio 100ml (MXN)"
                name="price"
                value={formData.price}
                onChange={handleChange}
                readOnly
                helpText="Determinado por tipo de perfume"
              />

              <NumberField
                label="Stock 100ml"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min={0}
                helpText="Unidades disponibles de 100ml"
              />
            </div>
          </div>

          {/* Presentación 35ml */}
          <div className="pt-4 border-t border-gray-700">
            <h4 className="text-sm font-medium text-neon-cyan mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-neon-cyan"></span>
              Presentación 35ml
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label="Precio 35ml (MXN)"
                name="price_40ml"
                value={formData.price_40ml}
                onChange={handleChange}
                min={0}
                helpText="Precio fijo para 35ml ($250)"
              />

              <NumberField
                label="Stock 35ml"
                name="stock_40ml"
                value={formData.stock_40ml}
                onChange={handleChange}
                min={0}
                helpText="Unidades disponibles de 35ml"
              />
            </div>
          </div>

          {/* Presentación 10ml: solo regalo de promociones */}
          <div className="pt-4 border-t border-gray-700">
            <h4 className="text-sm font-medium text-pink-400 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-pink-400"></span>
              Presentación 10ml (solo regalo de promociones)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField
                label="Stock 10ml"
                name="stock_10ml"
                value={formData.stock_10ml}
                onChange={handleChange}
                min={0}
                helpText="No se vende ni aparece en el catálogo; el cliente lo elige cuando su promoción incluye un 10ml gratis"
              />
            </div>
          </div>
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 8: IMÁGENES */}
        {/* ============================================ */}
        <SectionCard 
          title="Imágenes del Producto" 
          description="Sube las fotos del perfume"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ImageUploader
              value={formData.main_image}
              onChange={(url) => setFormData(prev => ({ ...prev, main_image: url }))}
              folder="products"
              label="Imagen Principal *"
              aspectRatio="1:1"
              previewHeight={250}
            />

            <ImageUploader
              multiple
              gallery={formData.gallery_images || []}
              onGalleryChange={(urls) => setFormData(prev => ({ ...prev, gallery_images: urls }))}
              folder="products"
              label="Galería de Imágenes"
              maxFiles={6}
            />
          </div>
        </SectionCard>

        {/* ============================================ */}
        {/* SECCIÓN 9: VISIBILIDAD */}
        {/* ============================================ */}
        <SectionCard 
          title="Visibilidad" 
          description="Controla si el producto está visible en la tienda"
        >
          <div className="flex">
            <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-5 h-5 accent-amber-500"
              />
              <div>
                <span className="text-sm text-white font-medium">Activo</span>
                <p className="text-xs text-gray-400">Visible en tienda</p>
              </div>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            💡 Para destacar este producto usa las "Etiquetas de Popularidad" de arriba (Destacado, Más vendidos, Nuevo Lanzamiento).
          </p>
        </SectionCard>

        {/* ============================================ */}
        {/* BOTONES DE ACCIÓN */}
        {/* ============================================ */}
        <div className="flex justify-end gap-4 pt-4">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="px-6 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            data-testid="cancel-button"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
            data-testid="save-button"
          >
            <Save size={18} />
            {saving ? 'Guardando...' : 'Guardar Producto'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProductForm;
